import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, Eye, EyeOff, ChevronUp, ChevronDown, GripVertical, X,
         Type, Image as ImageIcon, Award, BarChart3, Minus, MoveVertical, Columns, AlignCenter, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/utils/apiConfig';
const API = BACKEND_URL;

const BLOCK_TYPES = [
  { type: 'heading', label: 'Heading', icon: Type, description: 'Large title text' },
  { type: 'text', label: 'Text', icon: Type, description: 'Paragraph text' },
  { type: 'image', label: 'Image', icon: ImageIcon, description: 'Photo or graphic' },
  { type: 'badge', label: 'Badge', icon: Award, description: 'Small label tag' },
  { type: 'stats', label: 'Stats', icon: BarChart3, description: 'Number counters' },
  { type: 'image_overlay', label: 'Image Overlay', icon: Layers, description: 'Badge on image corner' },
  { type: 'divider', label: 'Divider', icon: Minus, description: 'Horizontal line' },
  { type: 'spacer', label: 'Spacer', icon: MoveVertical, description: 'Empty space' },
];

const PageSectionsManager = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [showBlockPicker, setShowBlockPicker] = useState(null); // {sectionId, column}
  const [uploading, setUploading] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchSections = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/page-sections/home`, { headers });
      setSections(res.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchSections(); }, []);

  const saveSection = async (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section) return;
    try {
      await axios.put(`${API}/api/admin/page-sections/${sectionId}`, {
        blocks: section.blocks,
        layout: section.layout,
        visible: section.visible
      }, { headers });
      toast.success('Saved');
    } catch (err) { toast.error('Save failed'); }
  };

  const addSection = async () => {
    try {
      const res = await axios.post(`${API}/api/admin/page-sections`, {
        page_name: 'home',
        layout: 'two_column',
        visible: true,
        blocks: []
      }, { headers });
      setSections([...sections, res.data]);
      setEditingSection(res.data.id);
      toast.success('Section added');
    } catch (err) { toast.error('Failed'); }
  };

  const deleteSection = async (id) => {
    if (!window.confirm('Delete this entire section?')) return;
    try {
      await axios.delete(`${API}/api/admin/page-sections/${id}`, { headers });
      setSections(sections.filter(s => s.id !== id));
      toast.success('Deleted');
    } catch (err) { toast.error('Failed'); }
  };

  const moveSection = async (idx, dir) => {
    const ns = [...sections];
    const swap = dir === 'up' ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= ns.length) return;
    [ns[idx], ns[swap]] = [ns[swap], ns[idx]];
    setSections(ns);
    try {
      await axios.put(`${API}/api/admin/page-sections/reorder`, { section_ids: ns.map(s => s.id) }, { headers });
    } catch (err) { fetchSections(); }
  };

  const updateLocal = (sectionId, updates) => {
    setSections(sections.map(s => s.id === sectionId ? { ...s, ...updates } : s));
  };

  // Block operations
  const addBlock = (sectionId, column, type) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section) return;
    const colBlocks = (section.blocks || []).filter(b => b.column === column);
    const newBlock = {
      id: crypto.randomUUID(),
      type,
      column,
      order: colBlocks.length,
      ...(type === 'heading' ? { text: 'New Heading' } : {}),
      ...(type === 'text' ? { text: 'New text block...' } : {}),
      ...(type === 'badge' ? { text: 'Label' } : {}),
      ...(type === 'image' ? { url: '' } : {}),
      ...(type === 'stats' ? { items: [{ value: '0', label: 'Label' }] } : {}),
      ...(type === 'image_overlay' ? { icon: 'award', title: 'Title', subtitle: '', corner: 'bottom-right' } : {}),
    };
    const newBlocks = [...(section.blocks || []), newBlock];
    updateLocal(sectionId, { blocks: newBlocks });
    setShowBlockPicker(null);
  };

  const removeBlock = (sectionId, blockId) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section) return;
    updateLocal(sectionId, { blocks: section.blocks.filter(b => b.id !== blockId) });
  };

  const updateBlock = (sectionId, blockId, updates) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section) return;
    const newBlocks = section.blocks.map(b => b.id === blockId ? { ...b, ...updates } : b);
    updateLocal(sectionId, { blocks: newBlocks });
  };

  const moveBlock = (sectionId, blockId, dir) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section) return;
    const block = section.blocks.find(b => b.id === blockId);
    const colBlocks = section.blocks.filter(b => b.column === block.column).sort((a, b) => a.order - b.order);
    const idx = colBlocks.findIndex(b => b.id === blockId);
    const swap = dir === 'up' ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= colBlocks.length) return;
    const newOrder = colBlocks[swap].order;
    colBlocks[swap].order = block.order;
    block.order = newOrder;
    updateLocal(sectionId, { blocks: [...section.blocks] });
  };

  const handleImageUpload = async (sectionId, blockId, file) => {
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/api/upload/review-image`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      updateBlock(sectionId, blockId, { url: res.data.image_url });
      toast.success('Uploaded');
    } catch (err) { toast.error('Upload failed'); }
    setUploading(false);
  };

  const resolveUrl = (url) => url && !url.startsWith('http') ? `${API}${url}` : url;

  // --- Block visual renderers ---

  const renderBlockEditor = (section, block) => {
    const sId = section.id;
    const bId = block.id;

    const blockHeader = (label) => (
      <div className="flex items-center justify-between mb-1.5 group/block">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{label}</span>
        {/* Always visible on touch devices; hover-reveal only on desktop */}
        <div className="flex items-center gap-1 sm:gap-0.5 opacity-100 sm:opacity-0 sm:group-hover/block:opacity-100 transition-opacity">
          <button onClick={() => moveBlock(sId, bId, 'up')} className="p-1.5 sm:p-0.5 hover:bg-gray-200 rounded" aria-label="Move block up">
            <ChevronUp className="w-4 h-4 sm:w-3 sm:h-3 text-gray-400" />
          </button>
          <button onClick={() => moveBlock(sId, bId, 'down')} className="p-1.5 sm:p-0.5 hover:bg-gray-200 rounded" aria-label="Move block down">
            <ChevronDown className="w-4 h-4 sm:w-3 sm:h-3 text-gray-400" />
          </button>
          <button onClick={() => removeBlock(sId, bId)} className="p-1.5 sm:p-0.5 hover:bg-red-50 rounded" aria-label="Remove block">
            <X className="w-4 h-4 sm:w-3 sm:h-3 text-red-400" />
          </button>
        </div>
      </div>
    );

    switch (block.type) {
      case 'heading':
        return (
          <div key={bId} className="mb-3 group/block">
            {blockHeader('Heading')}
            <input className="w-full text-lg font-bold text-slate-900 border-b-2 border-dashed border-gray-200 focus:border-cyan-400 outline-none py-1 bg-transparent"
              value={block.text || ''} onChange={e => updateBlock(sId, bId, { text: e.target.value })} />
          </div>
        );
      case 'text':
        return (
          <div key={bId} className="mb-3 group/block">
            {blockHeader('Text')}
            <textarea className="w-full text-sm text-slate-600 border border-dashed border-gray-200 focus:border-cyan-400 outline-none rounded p-2 min-h-[60px] resize-y bg-transparent"
              value={block.text || ''} onChange={e => updateBlock(sId, bId, { text: e.target.value })} />
          </div>
        );
      case 'badge':
        return (
          <div key={bId} className="mb-3 group/block">
            {blockHeader('Badge')}
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4 text-cyan-500" />
              <input className="border-b border-dashed border-gray-200 focus:border-cyan-400 outline-none text-sm text-cyan-700 font-semibold bg-transparent py-0.5"
                value={block.text || ''} onChange={e => updateBlock(sId, bId, { text: e.target.value })} />
            </div>
          </div>
        );
      case 'image':
        return (
          <div key={bId} className="mb-3 group/block">
            {blockHeader('Image')}
            {block.url ? (
              <div className="relative rounded-lg overflow-hidden">
                <img src={resolveUrl(block.url)} alt="" className="w-full h-32 object-cover" />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center gap-2 opacity-0 hover:opacity-100 transition-opacity">
                  <label className="bg-white text-gray-700 px-2 py-1 rounded text-xs cursor-pointer shadow">
                    Replace
                    <input type="file" accept="image/*" className="hidden"
                      onChange={e => handleImageUpload(sId, bId, e.target.files[0])} />
                  </label>
                  <button onClick={() => updateBlock(sId, bId, { url: '' })}
                    className="bg-red-500 text-white px-2 py-1 rounded text-xs shadow">Remove</button>
                </div>
              </div>
            ) : (
              <label className="block h-24 border-2 border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-cyan-400 transition-colors">
                <ImageIcon className="w-6 h-6 text-gray-300 mb-1" />
                <span className="text-[10px] text-gray-400">{uploading ? 'Uploading...' : 'Click to upload'}</span>
                <input type="file" accept="image/*" className="hidden"
                  onChange={e => handleImageUpload(sId, bId, e.target.files[0])} />
              </label>
            )}
            <input className="w-full mt-1 text-[10px] text-gray-400 border-b border-dashed border-gray-100 outline-none bg-transparent"
              placeholder="or paste URL..." value={block.url || ''} onChange={e => updateBlock(sId, bId, { url: e.target.value })} />
          </div>
        );
      case 'image_overlay':
        return (
          <div key={bId} className="mb-3 group/block p-2 bg-cyan-50 rounded-lg border border-cyan-200">
            {blockHeader('Image Overlay')}
            <div className="grid grid-cols-2 gap-1.5">
              <input className="border rounded px-2 py-1 text-xs" placeholder="Icon (award, users...)"
                value={block.icon || ''} onChange={e => updateBlock(sId, bId, { icon: e.target.value })} />
              <select className="border rounded px-2 py-1 text-xs bg-white"
                value={block.corner || 'bottom-right'} onChange={e => updateBlock(sId, bId, { corner: e.target.value })}>
                <option value="top-left">Top Left</option>
                <option value="top-right">Top Right</option>
                <option value="bottom-left">Bottom Left</option>
                <option value="bottom-right">Bottom Right</option>
              </select>
              <input className="border rounded px-2 py-1 text-xs" placeholder="Title"
                value={block.title || ''} onChange={e => updateBlock(sId, bId, { title: e.target.value })} />
              <input className="border rounded px-2 py-1 text-xs" placeholder="Subtitle"
                value={block.subtitle || ''} onChange={e => updateBlock(sId, bId, { subtitle: e.target.value })} />
            </div>
          </div>
        );
      case 'stats':
        return (
          <div key={bId} className="mb-3 group/block">
            {blockHeader('Stats')}
            <div className="space-y-1.5">
              {(block.items || []).map((stat, si) => (
                <div key={si} className="flex items-center gap-2">
                  <input className="w-16 border rounded px-2 py-1 text-sm font-bold text-center text-cyan-600"
                    value={stat.value} onChange={e => {
                      const ni = [...(block.items || [])]; ni[si] = { ...ni[si], value: e.target.value };
                      updateBlock(sId, bId, { items: ni });
                    }} />
                  <input className="flex-1 border rounded px-2 py-1 text-xs"
                    value={stat.label} onChange={e => {
                      const ni = [...(block.items || [])]; ni[si] = { ...ni[si], label: e.target.value };
                      updateBlock(sId, bId, { items: ni });
                    }} />
                  <button onClick={() => {
                    updateBlock(sId, bId, { items: (block.items || []).filter((_, i) => i !== si) });
                  }} className="text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              <button onClick={() => updateBlock(sId, bId, { items: [...(block.items || []), { value: '0', label: '' }] })}
                className="text-[10px] text-cyan-600 font-medium">+ Add Counter</button>
            </div>
          </div>
        );
      case 'divider':
        return <div key={bId} className="mb-3 group/block">{blockHeader('Divider')}<hr className="border-gray-200" /></div>;
      case 'spacer':
        return <div key={bId} className="mb-3 group/block">{blockHeader('Spacer')}<div className="h-4 bg-gray-50 rounded border border-dashed border-gray-200" /></div>;
      default:
        return null;
    }
  };

  // --- Block picker popup ---
  const renderBlockPicker = (sectionId, column) => (
    <div className="absolute z-50 bg-white rounded-xl shadow-2xl border p-3 w-56" style={{ bottom: '100%', left: 0, marginBottom: 8 }}>
      <div className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wide">Add Block</div>
      <div className="grid grid-cols-2 gap-1.5">
        {BLOCK_TYPES.map(bt => (
          <button key={bt.type}
            onClick={() => addBlock(sectionId, column, bt.type)}
            className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-gray-50 text-left transition-colors">
            <bt.icon className="w-4 h-4 text-gray-400 shrink-0" />
            <div>
              <div className="text-xs font-medium text-gray-700">{bt.label}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  // --- Section renderer ---
  const renderSection = (section, idx) => {
    const editing = editingSection === section.id;
    const blocks = section.blocks || [];
    const col0 = blocks.filter(b => b.column === 0).sort((a, b) => a.order - b.order);
    const col1 = blocks.filter(b => b.column === 1).sort((a, b) => a.order - b.order);
    const isTwoCol = section.layout === 'two_column';

    return (
      <div key={section.id} className={`rounded-xl border-2 transition-all ${editing ? 'border-cyan-300 shadow-lg' : 'border-gray-200'} ${!section.visible ? 'opacity-50' : ''}`}>
        {/* Toolbar (wraps on small screens, larger tap targets) */}
        <div className="flex flex-wrap items-center justify-between gap-y-2 px-3 py-2 bg-gray-50 border-b">
          <div className="flex items-center gap-2">
            <GripVertical className="w-4 h-4 text-gray-300 hidden sm:block" />
            <span className="text-xs font-semibold text-gray-500">Section {idx + 1}</span>
            {/* Layout toggle */}
            <div className="flex items-center gap-1 ml-2">
              <button onClick={() => { updateLocal(section.id, { layout: 'two_column' }); saveSection(section.id); }}
                className={`p-2 sm:p-1 rounded ${section.layout === 'two_column' ? 'bg-cyan-100 text-cyan-700' : 'text-gray-400 hover:bg-gray-200'}`} title="Two columns" aria-label="Two columns">
                <Columns className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
              </button>
              <button onClick={() => { updateLocal(section.id, { layout: 'single_column' }); saveSection(section.id); }}
                className={`p-2 sm:p-1 rounded ${section.layout === 'single_column' ? 'bg-cyan-100 text-cyan-700' : 'text-gray-400 hover:bg-gray-200'}`} title="Single column" aria-label="Single column">
                <AlignCenter className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-1 ml-auto">
            <button onClick={() => moveSection(idx, 'up')} disabled={idx === 0} className="p-2 sm:p-1 hover:bg-gray-200 rounded disabled:opacity-30" aria-label="Move section up">
              <ChevronUp className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
            </button>
            <button onClick={() => moveSection(idx, 'down')} disabled={idx === sections.length - 1} className="p-2 sm:p-1 hover:bg-gray-200 rounded disabled:opacity-30" aria-label="Move section down">
              <ChevronDown className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
            </button>
            <button onClick={() => updateLocal(section.id, { visible: !section.visible })} className="p-2 sm:p-1 hover:bg-gray-200 rounded" aria-label="Toggle visibility">
              {section.visible ? <Eye className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-green-500" /> : <EyeOff className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-gray-400" />}
            </button>
            <button onClick={() => { setEditingSection(editing ? null : section.id); if (editing) saveSection(section.id); }}
              className={`px-3 py-1.5 sm:px-2 sm:py-0.5 rounded text-xs font-medium ${editing ? 'bg-cyan-500 text-white' : 'hover:bg-gray-200 text-gray-600'}`} data-testid={`section-edit-toggle-${idx}`}>
              {editing ? 'Save' : 'Edit'}
            </button>
            <button onClick={() => deleteSection(section.id)} className="p-2 sm:p-1 hover:bg-red-50 rounded" aria-label="Delete section">
              <Trash2 className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-red-400" />
            </button>
          </div>
        </div>

        {/* Content area */}
        <div className="p-3 sm:p-4 bg-white">
          {editing ? (
            // --- EDITING: Show block editors in columns (stacked on mobile) ---
            <div className={isTwoCol ? 'grid grid-cols-1 sm:grid-cols-2 gap-4' : ''}>
              {/* Column 0 */}
              <div className={`min-h-[80px] ${isTwoCol ? 'sm:border-r border-dashed border-gray-100 sm:pr-4' : ''}`}>
                {isTwoCol && <div className="text-[9px] font-bold text-gray-300 uppercase tracking-widest mb-2">Left Column</div>}
                {col0.map(block => renderBlockEditor(section, block))}
                <div className="relative">
                  <button onClick={() => setShowBlockPicker(
                    showBlockPicker?.sectionId === section.id && showBlockPicker?.column === 0 ? null : { sectionId: section.id, column: 0 }
                  )} className="w-full py-2 border-2 border-dashed border-gray-200 rounded-lg text-gray-400 hover:border-cyan-400 hover:text-cyan-500 transition-colors text-xs font-medium">
                    <Plus className="w-4 h-4 inline mr-1" />Add Block
                  </button>
                  {showBlockPicker?.sectionId === section.id && showBlockPicker?.column === 0 && renderBlockPicker(section.id, 0)}
                </div>
              </div>

              {/* Column 1 (only in two_column) */}
              {isTwoCol && (
                <div className="min-h-[80px] sm:pl-4 pt-3 sm:pt-0 border-t sm:border-t-0 border-dashed border-gray-100">
                  <div className="text-[9px] font-bold text-gray-300 uppercase tracking-widest mb-2">Right Column</div>
                  {col1.map(block => renderBlockEditor(section, block))}
                  <div className="relative">
                    <button onClick={() => setShowBlockPicker(
                      showBlockPicker?.sectionId === section.id && showBlockPicker?.column === 1 ? null : { sectionId: section.id, column: 1 }
                    )} className="w-full py-2 border-2 border-dashed border-gray-200 rounded-lg text-gray-400 hover:border-cyan-400 hover:text-cyan-500 transition-colors text-xs font-medium">
                      <Plus className="w-4 h-4 inline mr-1" />Add Block
                    </button>
                    {showBlockPicker?.sectionId === section.id && showBlockPicker?.column === 1 && renderBlockPicker(section.id, 1)}
                  </div>
                </div>
              )}
            </div>
          ) : (
            // --- PREVIEW: Show a compact visual summary ---
            <div className={isTwoCol ? 'grid grid-cols-1 sm:grid-cols-2 gap-4' : ''} onClick={() => setEditingSection(section.id)}>
              <div className="cursor-pointer">
                {col0.map(b => {
                  if (b.type === 'badge') return <span key={b.id} className="inline-block px-2 py-0.5 bg-cyan-100 text-cyan-700 rounded-full text-[10px] font-semibold mb-1">{b.text}</span>;
                  if (b.type === 'heading') return <h4 key={b.id} className="text-base font-bold text-slate-900 mb-1">{b.text}</h4>;
                  if (b.type === 'text') return <p key={b.id} className="text-xs text-slate-500 line-clamp-2 mb-1">{b.text}</p>;
                  if (b.type === 'stats') return <div key={b.id} className="flex gap-3 mt-1">{(b.items || []).map((s, i) => <span key={i} className="text-xs"><strong className="text-cyan-600">{s.value}</strong> {s.label}</span>)}</div>;
                  if (b.type === 'divider') return <hr key={b.id} className="my-1 border-gray-200" />;
                  return null;
                })}
              </div>
              {isTwoCol && (
                <div className="cursor-pointer">
                  {col1.map(b => {
                    if (b.type === 'image' && b.url) return <img key={b.id} src={resolveUrl(b.url)} alt="" className="w-full h-20 object-cover rounded" />;
                    if (b.type === 'image_overlay') return <div key={b.id} className="text-[9px] text-cyan-600 font-semibold mt-1">{b.title} {b.subtitle}</div>;
                    return null;
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="page-sections-manager">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-800">Home Page Sections</h3>
          <p className="text-xs text-gray-400 mt-0.5">Build sections with blocks. Tap Edit on a section, use the arrows to reorder.</p>
        </div>
        <Button onClick={addSection} size="sm" data-testid="add-section-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Section
        </Button>
      </div>

      {loading ? (
        <p className="text-gray-400 text-center py-8">Loading...</p>
      ) : sections.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-xl">
          <Layers className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-400 mb-4">No sections yet</p>
          <Button onClick={addSection} size="sm" variant="outline">
            <Plus className="w-4 h-4 mr-1" /> Add First Section
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {sections.map((s, i) => renderSection(s, i))}
        </div>
      )}
    </div>
  );
};

export default PageSectionsManager;
